import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import { downloadFile } from '../../../functions/download-file';
import { DistributedReport } from '../../../models/api/reports/distributed-report';
import { Action } from '../../../models/core/action';
import { ReportsService } from '../../../services/reports/reports.service';
import { BaseComponent } from '../../base/base.component';
import { IconButtonComponent } from '../../core/buttons/icon-button.component';
import { ActionButtonComponent } from '../../core/buttons/action-button.component';
import { LinkLabelComponent } from '../../core/labels/link-label.component';
import { PageHeaderComponent } from '../../core/labels/page-header.component';
import { TagComponent } from '../../core/labels/tag.component';
import { MenuComponent } from '../../core/panels/menu.component';
import { ColumnComponent } from '../../core/table/column.component';
import { TableComponent } from '../../core/table/table.component';
import { FormatPipe } from '../../../pipes/format.pipe';

@Component({
  selector: 'app-distributed-reports',
  imports: [
    TableComponent,
    ColumnComponent,
    MenuComponent,
    CommonModule,
    IconButtonComponent,
    LinkLabelComponent,
    ActionButtonComponent,
    PageHeaderComponent,
    TagComponent,
    FormatPipe,
  ],
  template: `
    <div class="tw-pt-8">
      @if (showHeader) {
        <app-page-header
          headerSize="h3"
          label="Distributed reports"
          paddingClasses=""
        />
      }
      <div class="tw-pt-2">
        <app-table
          [dataSource]="reports"
          [showSelection]="false"
          selectionKey="reportId"
          [showActions]="false"
          recordLabel="distributed report"
          dataKey="reportId"
          scrollHeight="2000px"
          [stripedRows]="false"
          [showRefresh]="true"
          [loading]="loading"
          [showFilter]="false"
          [showFilterButton]="false"
          sortField="reportCompleted"
          [sortOrder]="-1"
          (refresh)="refresh()"
        >
          <app-column
            name="reportName"
            label="Name"
            filterType="text"
            [wrap]="true"
            minWidth="320px"
            maxWidth="420px"
          >
            <ng-template let-data>
              <div
                class="tw-flex tw-items-center tw-gap-2 tw-max-w-full tw-break-words tw-whitespace-normal"
              >
                <app-link-label
                  class="tw-min-w-0"
                  [href]="getReportDetailHref(data)"
                  (click)="viewReportDetails(data)"
                  style="cursor: pointer;"
                >
                  {{ data.reportName }}
                </app-link-label>
                <app-tag type="Purple">
                  {{ data.fileFormat }}
                </app-tag>
                <app-icon-button
                  class="tw-hidden md:tw-inline-flex"
                  icon="fa-regular fa-arrow-down-to-line"
                  tooltip="Click to download report"
                  (click)="downloadReport(data)"
                />
              </div>
            </ng-template>
          </app-column>
          <app-column name="reportBase" label="Base report" filterType="select"
            ><ng-template let-data
              ><div class="tw-max-w-full tw-break-words tw-whitespace-normal">
                {{ data.reportBase }}
              </div>
            </ng-template>
          </app-column>
          <app-column
            name="reportCompleted"
            label="Completed"
            format="dt"
            filterType="date"
            ><ng-template let-data
              ><div class="tw-max-w-full tw-break-words tw-whitespace-normal">
                {{ data.reportCompleted | format: 'dt' }}
              </div>
            </ng-template>
          </app-column>
          <app-column
            name="actions"
            label="Actions"
            classes=""
            [frozen]="true"
            [sort]="false"
          >
            <ng-template let-data>
              <div class="tw-max-w-full tw-break-words tw-whitespace-normal">
                <app-action-button
                  [ariaLabel]="'More actions for ' + data.reportName"
                  [menu]="rowMenu"
                  [row]="data"
                  (click)="rowMenu.toggle($event, data)"
                />
              </div>
            </ng-template>
          </app-column>
        </app-table>
      </div>
    </div>
    <app-menu
      [items]="rowActions"
      #rowMenu
      (click)="onAction($event, rowMenu.data)"
    />
  `,
  styles: ``,
})
export class DistributedReportsComponent
  extends BaseComponent
  implements OnInit
{
  private readonly reportsService = inject(ReportsService);

  @Input()
  showHeader = true;

  @Input()
  externalAccess = false;

  loading = false;

  rowActions: Action[] = [
    { label: 'View details', key: 'view' },
    { label: 'Download', key: 'download' },
  ];

  reports: DistributedReport[] = [];

  async ngOnInit(): Promise<void> {
    this.dispose.subs = this.reportsService.reportsLoaded.subscribe(
      (result) => {
        this.reports = result.distributedReports;
      },
    );

    await this.loadReports(false);
  }

  async onAction(action: Action, report: DistributedReport) {
    if (action.key === 'view') {
      this.viewReportDetails(report);
    } else if (action.key === 'download') {
      await this.downloadReport(report);
    }
  }

  getReportDetailHref(report: DistributedReport): string {
    return this.reportsService.getDetailUrl(
      report,
      'distributed',
      this.externalAccess,
    );
  }

  viewReportDetails(report: DistributedReport) {
    this.reportsService.navigateToDetail(
      report,
      'distributed',
      this.externalAccess,
    );
  }

  async downloadReport(report: DistributedReport): Promise<void> {
    const blob = await this.api.reports.downloadDistributedReport(
      report.sourceSystemReportId,
      report.sourceSystemCode,
      report.fileName,
    );

    downloadFile(blob, report.fileName, blob.type);
  }

  async refresh(): Promise<void> {
    await this.loadReports(true);
  }

  private async loadReports(refreshCache: boolean): Promise<void> {
    this.loading = true;
    try {
      const result = this.externalAccess
        ? await this.reportsService.getExternalReports(refreshCache)
        : await this.reportsService.getReports(refreshCache);
      this.reports = result.distributedReports;
    } finally {
      this.loading = false;
    }
  }
}
